// FormContext.js
import React, { createContext, useContext, useMemo } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useForm } from '@mantine/form'

import { navigate, routes, useLocation, useParams } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import ApplicationLoadingScreen from 'src/components/Feedback/ApplicationLoadingScreen/ApplicationLoadingScreen'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_APPLICATION,
  MARK_APPLICATION_AS_SUBMITTED,
  UPDATE_APPLICATION,
} from 'src/graphql/application.graphql'
import { MARK_CONSENTS_AS_CONFIRMED } from 'src/graphql/consent.graphql'
import { userFacingMutationError } from 'src/lib/userFacingMutationError'

import { savedFormValues, validate } from './form'
import { filterPrivateQuestionResponses } from './questionResponses'
import { checkSections } from './stepChecks'
import type { SectionIssues } from './stepChecks'
import { validateAllFields } from './validators'

const FormLoader = ({ application, children }) => {
  //Initialize form with the application data
  const form = useForm({
    validateInputOnBlur: true,
    initialValues: savedFormValues(application),
    validate,
  })

  //Return the form context
  return <FormContext.Provider value={form}>{children}</FormContext.Provider>
}

const FormContext = createContext(null)

export const FormProvider = ({ children }) => {
  const { applicationId } = useParams()
  const { currentUser } = useAuth()

  const { data: applicationResponse, loading } = useQuery(GET_APPLICATION, {
    variables: {
      id: applicationId,
    },
    onCompleted: (data) => {
      if (data?.application === null) {
        window.location.href = '/404'
        return
      }
      if (
        data?.application?.applicationId &&
        data.application.applicationId !== applicationId
      ) {
        navigate(
          `/driver-application?applicationId=${data.application.applicationId}`
        )
      }
      datadogRum.setUser({
        id: currentUser.userId,
        email: currentUser.email,
        phone: currentUser.phone,
        roles: currentUser.roles,
        applicantId: data?.application?.applicant?.applicantId,
        applicationId: data?.application?.applicationId,
        company: data?.application.company.companyId,
        companyName: data?.application.company.companyName,
      })
    },
    onError: () => {
      navigate(routes.forbidden())
    },
  })

  if (loading) return <ApplicationLoadingScreen />

  return (
    <>
      {applicationResponse && (
        <FormLoader application={applicationResponse.application}>
          {children}
        </FormLoader>
      )}
    </>
  )
}

const SECTION_SCREENS = [
  'driver-application',
  'address-information',
  'license-information',
  'equipment-information',
  'accident-history',
  'employment-history',
  'company-questions',
]

export const useFormContext = () => {
  const form = useContext(FormContext)
  const { applicationId, from } = useParams()
  const { pathname } = useLocation()
  const currentScreen = pathname.split('/').pop()
  // Opened from the Review page: the step is an edit of one section, so both
  // ways out lead back there instead of along the step chain. Signing keeps
  // the marker so Back from consents still returns to Review.
  const fromReview = from === 'review'
  const reviewPath = `/driver-application/review?applicationId=${applicationId}`
  const signingQuery = `applicationId=${applicationId}${
    fromReview ? '&from=review' : ''
  }`
  const backToReview =
    fromReview &&
    (SECTION_SCREENS.includes(currentScreen) || currentScreen === 'consents')
  const backText = backToReview ? 'Back to Review' : 'Back'

  // Get application data to check for custom questions
  const { data: applicationResponse } = useQuery(GET_APPLICATION, {
    variables: {
      id: applicationId,
    },
    onCompleted: (data) => {
      if (data?.application === null) {
        window.location.href = '/404'
        return
      }
    },
    onError: () => {
      navigate(routes.forbidden())
    },
  })

  const application = applicationResponse?.application || null
  // PUBLIC (short form) questions don't count — the company-questions step
  // only exists when the application has PRIVATE questions to answer.
  const hasCustomQuestions =
    filterPrivateQuestionResponses(application?.questionResponses).length > 0

  // What the server has, checked the way each step's Continue checks it. The
  // signing screens refuse while anything here is outstanding.
  const sectionIssues: SectionIssues | null = useMemo(
    () =>
      application
        ? checkSections(savedFormValues(application), application)
        : null,
    [application]
  )
  const hasIssues = sectionIssues
    ? Object.values(sectionIssues).some((messages) => messages.length > 0)
    : false

  const goBack = () => {
    if (backToReview) {
      navigate(reviewPath)
      return
    }
    switch (currentScreen) {
      case 'address-information':
        navigate(`/driver-application?applicationId=${applicationId}`)
        break
      case 'license-information':
        navigate(
          `/driver-application/address-information?applicationId=${applicationId}`
        )
        break
      case 'equipment-information':
        navigate(
          `/driver-application/license-information?applicationId=${applicationId}`
        )
        break
      case 'accident-history':
        navigate(
          `/driver-application/equipment-information?applicationId=${applicationId}`
        )
        break
      case 'employment-history':
        navigate(
          `/driver-application/accident-history?applicationId=${applicationId}`
        )
        break
      case 'company-questions':
        navigate(
          `/driver-application/employment-history?applicationId=${applicationId}`
        )
        break
      case 'consents':
        if (hasCustomQuestions) {
          navigate(
            `/driver-application/company-questions?applicationId=${applicationId}`
          )
        } else {
          navigate(
            `/driver-application/employment-history?applicationId=${applicationId}`
          )
        }
        break
      case 'sign':
        navigate(`/driver-application/consents?${signingQuery}`)
        break
      case 'submit':
        navigate(`/driver-application/sign?${signingQuery}`)
        break
      default:
        navigate(`/driver-application?applicationId=${applicationId}`)
        break
    }
  }

  const handleNavigation = () => {
    if (fromReview && SECTION_SCREENS.includes(currentScreen)) {
      navigate(reviewPath)
      return
    }
    switch (currentScreen) {
      case 'driver-application':
        navigate(
          `/driver-application/address-information?applicationId=${applicationId}`
        )
        break
      case 'address-information':
        navigate(
          `/driver-application/license-information?applicationId=${applicationId}`
        )
        break
      case 'license-information':
        navigate(
          `/driver-application/equipment-information?applicationId=${applicationId}`
        )
        break
      case 'equipment-information':
        navigate(
          `/driver-application/accident-history?applicationId=${applicationId}`
        )
        break
      case 'accident-history':
        navigate(
          `/driver-application/employment-history?applicationId=${applicationId}`
        )
        break
      case 'employment-history':
        // Always route through company-questions: the screen's on-mount sync
        // is what CREATES response rows for template questions added after
        // this application existed. Deciding here from the rows we already
        // have would skip those new questions entirely. When nothing PRIVATE
        // exists after the sync, the screen redirects to consents itself
        // (with replace, so it doesn't linger in history).
        navigate(
          `/driver-application/company-questions?applicationId=${applicationId}`
        )
        break
      case 'company-questions':
        navigate(`/driver-application/consents?applicationId=${applicationId}`)
        break
      case 'consents':
        navigate(`/driver-application/sign?${signingQuery}`)
        break
      case 'sign':
        navigate(`/driver-application/submit?applicationId=${applicationId}`)
        break
      default:
        navigate(`/driver-application?applicationId=${applicationId}`)
        break
    }
  }

  /**
   * Saving without leaving the page.
   *
   * The mutation below navigates when it completes, which is right for a
   * screen's Continue and wrong for saving one entry at a time — a driver who
   * finishes an employer would be thrown to the next screen. Same mutation, no
   * navigation, and the error surfaces to the caller rather than only a toast.
   */
  const [saveProgress, { loading: saveProgressLoading }] = useMutation(
    UPDATE_APPLICATION,
    {
      onCompleted: () => {
        form.resetDirty()
      },
      onError: () => {
        toast('Unable to save. Please try again.', 'error')
      },
      refetchQueries: [
        { query: GET_APPLICATION, variables: { id: applicationId } },
      ],
      awaitRefetchQueries: true,
    }
  )

  const [updateApplication, { loading: updateLoading }] = useMutation(
    UPDATE_APPLICATION,
    {
      onCompleted: () => {
        form.resetDirty()
        handleNavigation()
      },
      onError: (error) => {
        toast(
          userFacingMutationError(
            error,
            'Unable to save application. Please try again later.'
          ),
          'error'
        )
      },
      refetchQueries: [
        {
          query: GET_APPLICATION,
          variables: {
            id: applicationId,
          },
        },
      ],
    }
  )

  const [markApplicationAsSubmitted] = useMutation(
    MARK_APPLICATION_AS_SUBMITTED,
    {
      variables: {
        id: applicationId,
      },
      onCompleted: () => {
        toast('Application updated successfully', 'success')
        handleNavigation()
      },
      onError: (error) => {
        toast(
          userFacingMutationError(
            error,
            'Unable to save application. Please try again later.'
          ),
          'error'
        )
      },
    }
  )

  const onSignatureComplete = () => {
    markApplicationAsSubmitted()
  }

  const [markConsentsAsConfirmed, { loading: loadingUpdateConsents }] =
    useMutation(MARK_CONSENTS_AS_CONFIRMED, {
      variables: {
        applicationId,
      },
      onCompleted: () => {
        handleNavigation()
      },
      onError: () => {
        toast('Unable to save application. Please try again later.', 'error')
      },
    })

  const onConsentsConfirmed = () => {
    markConsentsAsConfirmed()
  }

  const handleNext = (input) => {
    const validationHasError = validateAllFields(form, input)
    if (validationHasError) {
      // Say something. A silent return leaves the driver pressing a button that
      // reports nothing, which is exactly how a schema disagreeing with a screen
      // becomes an afternoon of guessing.
      toast(
        'Some answers are missing or need correcting. Check the highlighted fields.',
        'error'
      )
    }
    if (!validationHasError) {
      //TODO: check what happens to the inputs if this fails, may have to refetch the application
      updateApplication({
        variables: {
          id: applicationId,
          input: input,
        },
      })
    }
  }

  /**
   * Save a step's payload without moving on, for leaving through a side exit.
   * Resolves true only when validation passed and the server accepted it.
   */
  const saveSection = async (input): Promise<boolean> => {
    if (validateAllFields(form, input)) {
      toast(
        'Some answers are missing or need correcting. Check the highlighted fields.',
        'error'
      )
      return false
    }
    const result = await saveProgress({
      variables: { id: applicationId, input },
    })
    if (!result?.data) return false
    form.resetDirty()
    return true
  }

  const discardChanges = () => {
    form.setValues(savedFormValues(application))
    form.resetDirty()
    form.clearErrors()
  }

  const goToReview = (options?: { replace?: boolean }) =>
    navigate(reviewPath, options)

  return {
    form,
    goBack,
    handleNext,
    saveSection,
    discardChanges,
    goToReview,
    fromReview,
    backText,
    sectionIssues,
    hasIssues,
    hasUnsavedChanges: form.isDirty(),
    saveProgress,
    saveProgressLoading,
    updateLoading,
    onSignatureComplete,
    onConsentsConfirmed,
    loadingUpdateConsents,
    applicationId,
    application,
  }
}
